<template>
  <v-container container class="ActivateAccount-container mx-auto">
    <template v-if="!loadingPage">
      <LoginTopbar/>
      <div :class="$vuetify.display.smAndDown ? '' : 'ActivateAccount-center-box'">
        <v-row class="ActivateAccount-content">
          <v-col class="ActivateAccount-colonne-explain">
            <ClosdTagLine/>
          </v-col>
          <v-col>
            <AppCard
              :elevation="0"
              class="ActivateAccount-card"
            >
              <v-row>
                <v-col>
                  <h2>{{ $t('login.views.ActivateAccount.title') }}</h2>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <PasswordTextField
                    v-model="v$.login.password.$model"
                    :label="$t('login.views.ActivateAccount.password')"
                    class="ActivateAccount-input"
                    :error-messages="passwordErrors"
                    maxlength="50"
                    @blur="validatePassword"
                    @keypress.enter="prepareActivateAccount"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-n6 mb-4">
                <v-col>
                  <PasswordRulesDisplay
                    :password="login.password"
                    :is-not-previous-password="isNotPreviousPassword"
                    class="mt-2"
                    @rules-validity-changed="isFormValid = $event"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <PasswordTextField v-model="v$.login.passwordConfirmation.$model"
                                     :label="$t('login.views.ActivateAccount.confirmPassword')"
                                     class="ActivateAccount-input mt-n3"
                                     :error-messages="passwordConfirmErrors"
                                     maxlength="50"
                                     @blur="validatePasswordConfirmation"
                                     @keypress.enter="prepareActivateAccount"
                  >
                  </PasswordTextField>
                </v-col>
              </v-row>
              <v-row class="mt-n4">
                <v-col>
                  <v-checkbox v-model.trim="v$.login.acceptPrivacy.$model"
                              outlined
                              class="ActivateAccount-checkbox"
                              :error-messages="privacyErrors"
                              @update:model-value="validatePrivacy"
                  >
                    <template v-slot:label>
                      <i18n-t keypath="login.views.ActivateAccount.privacyAcceptance"
                              tag="span"
                              class="ActivateAccount-fs"
                              scope="global"
                      >
                        <template #privacyLinkText>
                          <a href="#" @click.stop>
                            <AppDownloadFile :link="getResourceLink($t('login.views.ActivateAccount.privacyUrl'))"
                                             forceReader>
                              <template #default="{ submitForm }">
                                <div style="color: rgb(var(--v-theme-anchor))" @click="submitForm">
                                  {{ $t('login.views.ActivateAccount.privacyLinkText') }}
                                </div>
                              </template>
                            </AppDownloadFile>
                          </a>
                        </template>
                      </i18n-t>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-checkbox v-model.trim="v$.login.acceptTOS.$model"
                              outlined
                              class="ActivateAccount-checkbox"
                              :error-messages="tosErrors"
                              @update:model-value="validateTos"
                  >
                    <template v-slot:label>
                      <i18n-t keypath="login.views.ActivateAccount.tosAcceptance"
                              tag="span"
                              class="ActivateAccount-fs"
                              scope="global"
                      >
                        <template #tosLinkText>
                          <a href="#" @click.stop>
                            <AppDownloadFile :link="getResourceLink($t('login.views.ActivateAccount.tosUrl'))"
                                             forceReader>
                              <template #default="{ submitForm }">
                                <div style="color: rgb(var(--v-theme-anchor))" @click="submitForm">
                                  {{ $t('login.views.ActivateAccount.tosLinkText') }}
                                </div>
                              </template>
                            </AppDownloadFile>
                          </a>
                        </template>
                      </i18n-t>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row class="mt-n3">
                <v-col class="text-center">
                    <AppButton color="primary"
                               :loading="loading"
                               @click="prepareActivateAccount"
                    >
                      {{ $t('login.views.ActivateAccount.activateAccountBtn') }}
                    </AppButton>
                </v-col>
              </v-row>
            </AppCard>

            <div class="text-center mt-6">
              <BackToClosd />
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
    <template v-if="loadingPage">
      <div class="d-flex align-center justify-center">
        <v-progress-circular
          style="width: 64px; height: 64px; position: absolute;"
          class="ActivateAccount-loader"
          color="primary"
          indeterminate
          size="64"
        />
      </div>
    </template>
  </v-container>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { createNamespacedHelpers } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import PasswordRulesDisplay from '@/common/PasswordRulesDisplay.vue'
import PasswordTextField from '@/common/PasswordTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { Login } from '@/models/login.model'
import { ROOM_ROUTE_TYPES, SIGNING_VIEW_ROUTE_NAME } from '@/router/routerData'
import { ACTIVATE_ACCOUNT, CHECK_TOKEN } from '@/store/modules/login/action_types'
import { SET_LOADING_PAGE } from '@/store/modules/login/mutation_types'

import BackToClosd from '../BackToClosd.vue'
import ClosdTagLine from '../ClosdTagLine.vue'
import LoginTopbar from '../LoginTopbar.vue'

const { mapState: mapLoginState, mapActions: mapLoginActions } = createNamespacedHelpers('login')

export default {
  name: 'ActivateAccount',
  components: {
    AppButton,
    AppCard,
    AppDownloadFile,
    BackToClosd,
    ClosdTagLine,
    LoginTopbar,
    PasswordTextField,
    PasswordRulesDisplay,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      login: {
        password: {
          required,
        },
        passwordConfirmation: {
          required,
          sameAs: sameAs(this.login.password),
        },
        acceptPrivacy: {
          sameAs: sameAs(true),
        },
        acceptTOS: {
          sameAs: sameAs(true),
        },
      },
    }
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      login: new Login('', '', false, '', '', '', this.token),
      isNotPreviousPassword: undefined,
      isFormValid: false,
    }
  },
  computed: {
    ...mapLoginState(['loading', 'loadingPage']),
    passwordErrors () {
      return formFieldErrors(
        this.v$.login.password,
        [
          [
            'required',
            this.$t(
              'login.views.ActivateAccount.rules.password.required',
              { fieldName: this.$t('login.views.ActivateAccount.password') },
            ),
          ],
        ],
      )
    },
    passwordConfirmErrors () {
      return formFieldErrors(
        this.v$.login.passwordConfirmation,
        [
          ['sameAs', this.$t('login.views.ActivateAccount.rules.confirmPassword.equals', { fieldName: this.$t('login.views.ActivateAccount.confirmPassword') })],
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.ActivateAccount.confirmPassword') })],
        ],
      )
    },
    privacyErrors () {
      return formFieldErrors(
        this.v$.login.acceptPrivacy,
        [
          ['sameAs', this.$t('login.views.ActivateAccount.checkboxRequired', { fieldName: this.$t('login.views.ActivateAccount.privacy') })],
        ],
      )
    },
    tosErrors () {
      return formFieldErrors(
        this.v$.login.acceptTOS,
        [
          ['sameAs', this.$t('login.views.ActivateAccount.checkboxRequired', { fieldName: this.$t('login.views.ActivateAccount.tos') })],
        ],
      )
    },
  },
  created () {
    this.$store.commit(`login/${SET_LOADING_PAGE}`, true)
    this.checkInitToken()
  },
  methods: {
    ...mapLoginActions([ACTIVATE_ACCOUNT, CHECK_TOKEN]),
    checkInitToken () {
      this.CHECK_TOKEN(this.token)
    },
    validatePassword () {
      this.v$.login.password.$touch()
    },
    validatePasswordConfirmation () {
      this.v$.login.passwordConfirmation.$touch()
    },
    validatePrivacy () {
      this.v$.login.acceptPrivacy.$touch()
    },
    validateTos () {
      this.v$.login.acceptTOS.$touch()
    },
    getResourceLink (fileName) {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=${fileName}`
    },
    async prepareActivateAccount () {
      if (this.loading) {
        return
      }
      this.v$.login.$touch()
      if (!this.v$.login.$invalid) {
        try {
          const redirectRoute = this.$route.query.envelopeIdToSign &&
            {
              name: SIGNING_VIEW_ROUTE_NAME,
              params: {
                envelopeId: this.$route.query.envelopeIdToSign,
                mnemo: this.$route.query.mnemo,
                roomType: ROOM_ROUTE_TYPES.PROJECT,
              },
            }
          await this.ACTIVATE_ACCOUNT({
            login: this.login,
            redirectRoute,
          })
          this.isNotPreviousPassword = true
        } catch (e) {
          console.error(e)
          if (
            e.response?.data?.validationErrors &&
            e.response.data.validationErrors.includes('PASSWORD_SAME_AS_ONE_OF_PREVIOUS_PASSWORDS')
          ) {
            this.isNotPreviousPassword = false
            toast.error(this.$t('login.views.ActivateAccount.previousPasswordError'))
          } else
          toast.error(this.$t('login.views.ActivateAccount.submitError'))
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "vuetify/settings";

  .ActivateAccount-container {
    max-width: 1100px;
    height: 100%;
  }
  .ActivateAccount-loader{
    margin-top: 50%;
    transform: translateY(-75%);
  }
  .ActivateAccount-card {
    box-shadow: 0 0 10px 0 rgba(94, 86, 105, 0.1) !important;
    border: 1px solid #bdbdbd;
    padding: 25px 35px;
    position: relative;
  }
  .ActivateAccount-fs{
    font-size: 14px;
  }
  .ActivateAccount-font-color{
    color: #6c757d;
  }
  :deep(.ActivateAccount-input .v-input__slot) {
    background-color: #fff;
  }
  :deep(.ActivateAccount-checkbox .fa-square:before) {
      background-color: #fff;
  }
  :deep(.theme--light.v-btn.v-btn--disabled.v-btn--has-bg) {
      background-color: #ffe3e1 !important;
  }
  .ActivateAccount-link{
      text-decoration: none;
  }
  .ActivateAccount-center-box{
    display: flex;
    align-items: center;
    justify-content: center;
    height: 90%;
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')}{
    .ActivateAccount-colonne-explain{
      display: none;
    }
    .ActivateAccount-content{
      margin-top: 0;
      transform: translateY(0);
    }
  }
</style>
